(() => {
  /*
   * Install the delegated handlers only once. Event delegation means the
   * controls continue to work when Shopify replaces section HTML.
   */
  if (window.productMediaControlsInstalled) return;
  window.productMediaControlsInstalled = true;

  const COLOR_OPTION_NAMES = new Set(['Color', 'Colour']);

  /*
   * Preserve the global functions used by any existing inline quantity-button
   * onclick attributes. Dispatch events because changing .value in JavaScript
   * does not trigger input/change by itself.
   */
  function changeQuantity(amount) {
    const input = document.querySelector('input[name="quantity"]');
    if (!input) return;

    const currentValue = Number.parseInt(input.value, 10);
    const configuredMinimum = Number(input.dataset.min || input.min);
    const minimum = Number.isFinite(configuredMinimum)
      ? configuredMinimum
      : 1;
    const nextValue = Math.max(
      minimum,
      (Number.isFinite(currentValue) ? currentValue : minimum) + amount
    );

    input.value = String(nextValue);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  window.productAdd = () => changeQuantity(1);
  window.productMinus = () => changeQuantity(-1);

  function getProductSection(element) {
    return element.closest('[id^="MainProduct-"]') || document;
  }

  function findColorRadio(section, value) {
    return Array.from(
      section.querySelectorAll('input[type="radio"]')
    ).find(
      (radio) =>
        COLOR_OPTION_NAMES.has(radio.name) &&
        radio.value.trim() === value.trim()
    );
  }

  function getThumbnailVariantName(thumbnail) {
    const alt = thumbnail.querySelector('img')?.alt?.trim();

    if (!alt) return '';

    /*
     * Existing image alts use "Product name - Variant name".
     * If there is no separator, use the whole alt as the option value.
     */
    return alt.includes('- ') ? alt.split('- ').pop().trim() : alt;
  }

  function pauseMedia(container) {
    container.querySelectorAll('video').forEach((video) => video.pause());

    container.querySelectorAll('iframe').forEach((iframe) => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'pauseVideo',
          args: [],
        }),
        '*'
      );
    });
  }

  function showVideo(button) {
    const videoContainer = button.closest('.video-container');
    if (!videoContainer) return;

    const selectedIndex = Number(button.dataset.videobtn) - 1;
    /*
     * The theme renders product videos in two ways:
     * - the popup modal uses .iframe_container
     * - the inline/mobile version uses .simple-youtube-embed
     */
    const videos = Array.from(
      videoContainer.querySelectorAll(
        '.iframe_container, .simple-youtube-embed'
      )
    );
    const buttons = Array.from(
      videoContainer.querySelectorAll('.video-label[data-videobtn]')
    );

    if (
      !Number.isInteger(selectedIndex) ||
      selectedIndex < 0 ||
      selectedIndex >= videos.length
    ) {
      return;
    }

    buttons.forEach((item) => {
      const isSelected = item === button;

      item.classList.toggle('video-label-showed', isSelected);
      item.setAttribute('aria-pressed', String(isSelected));
    });

    videos.forEach((videoWrapper, index) => {
      const isSelected = index === selectedIndex;

      videoWrapper.hidden = !isSelected;
      videoWrapper.style.display = isSelected ? '' : 'none';
      videoWrapper.setAttribute('aria-hidden', String(!isSelected));

      if (!isSelected) pauseMedia(videoWrapper);
    });
  }

  function initializeVideoControls(root = document) {
    root.querySelectorAll('.video-container').forEach((videoContainer) => {
      const selectedButton =
        videoContainer.querySelector(
          '.video-label.video-label-showed[data-videobtn]'
        ) ||
        videoContainer.querySelector('.video-label[data-videobtn]');

      if (selectedButton) showVideo(selectedButton);
    });
  }

  /*
   * Fallback for the theme's ModalDialog constructor error. The normal custom
   * element keeps control when it initialized successfully; these functions
   * only supply the missing open/close behavior.
   */
  function openProductVideoModal(opener) {
    const selector = opener.dataset.modal;
    if (!selector) return;

    let modal;

    try {
      modal = document.querySelector(selector);
    } catch (error) {
      console.warn(`Invalid product video modal selector: ${selector}`);
      return;
    }

    if (!modal || modal.hasAttribute('open')) return;

    modal.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    modal.querySelector('[role="dialog"]')?.focus();
  }

  function closeProductVideoModal(closeButton) {
    const modal = closeButton.closest(
      'modal-dialog.product-popup-modal'
    );

    if (!modal) return;

    pauseMedia(modal);
    modal.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  }

  /*
   * Fallback for galleries that were rendered before Shopify's
   * <media-gallery> custom element initialized. If the normal gallery click
   * handler already selected the requested media, this exits without doing
   * anything.
   */
  function ensureGalleryMediaIsActive(thumbnailButton) {
    const thumbnailItem = thumbnailButton.closest('[data-target]');
    const gallery = thumbnailButton.closest('media-gallery');
    const mediaId = thumbnailItem?.dataset.target;
    const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
    const thumbnails = gallery?.querySelector(
      '[id^="GalleryThumbnails"]'
    );

    if (!mediaId || !viewer) return;

    const mediaItems = Array.from(
      viewer.querySelectorAll('[data-media-id]')
    );
    const requestedMedia = mediaItems.find(
      (item) => item.dataset.mediaId === mediaId
    );

    if (!requestedMedia) return;

    const alreadySelected =
      requestedMedia.classList.contains('is-active') &&
      thumbnailButton.getAttribute('aria-current') === 'true';

    if (alreadySelected) return;

    mediaItems.forEach((item) => item.classList.remove('is-active'));
    requestedMedia.classList.add('is-active');

    thumbnails
      ?.querySelectorAll('button[aria-current]')
      .forEach((button) => button.removeAttribute('aria-current'));
    thumbnailButton.setAttribute('aria-current', 'true');

    requestedMedia.parentElement?.scrollTo?.({
      left: requestedMedia.offsetLeft,
      behavior: 'smooth',
    });

    const thumbnailScroller =
      thumbnails?.slider ||
      thumbnails?.querySelector('.thumbnail-list, .slider');

    thumbnailScroller?.scrollTo?.({
      left: thumbnailItem.offsetLeft,
      behavior: 'smooth',
    });

    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }

    const deferredMedia =
      requestedMedia.querySelector('.deferred-media');

    if (
      deferredMedia &&
      typeof deferredMedia.loadContent === 'function'
    ) {
      deferredMedia.loadContent(false);
    }
  }

  function updateGalleryArrowState(component, intendedLeft) {
    const slider = component?.querySelector('.slider');
    if (!slider) return;

    const maximumLeft = Math.max(
      0,
      slider.scrollWidth - slider.clientWidth
    );
    const currentLeft =
      typeof intendedLeft === 'number'
        ? intendedLeft
        : slider.scrollLeft;
    const previousButton = component.querySelector(
      '.slider-button--prev'
    );
    const nextButton = component.querySelector(
      '.slider-button--next'
    );

    if (previousButton) {
      previousButton.disabled = currentLeft <= 1;
    }

    if (nextButton) {
      nextButton.disabled = currentLeft >= maximumLeft - 1;
    }
  }

  function moveGalleryThumbnailSlider(button) {
    const component = button.closest('slider-component');

    if (
      !component ||
      !component.id.startsWith('GalleryThumbnails')
    ) {
      return;
    }

    const slider = component.querySelector('.slider');
    const thumbnails = Array.from(
      slider?.querySelectorAll('[data-target]') || []
    );

    if (!slider || thumbnails.length === 0) return;

    const direction = button.classList.contains(
      'slider-button--next'
    )
      ? 1
      : -1;
    const requestedStep = Number(button.dataset.step);
    const step = Number.isFinite(requestedStep)
      ? Math.max(1, requestedStep)
      : 1;
    const firstOffset = thumbnails[0].offsetLeft;
    const secondOffset = thumbnails[1]?.offsetLeft;
    const itemDistance =
      typeof secondOffset === 'number'
        ? Math.abs(secondOffset - firstOffset)
        : thumbnails[0].offsetWidth;
    const maximumLeft = Math.max(
      0,
      slider.scrollWidth - slider.clientWidth
    );
    const targetLeft = Math.min(
      maximumLeft,
      Math.max(
        0,
        slider.scrollLeft + direction * itemDistance * step
      )
    );

    if (typeof slider.scrollTo === 'function') {
      slider.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });
    } else {
      slider.scrollLeft = targetLeft;
    }

    updateGalleryArrowState(component, targetLeft);
  }

  function initializeGalleryArrowButtons(root = document) {
    root
      .querySelectorAll(
        'slider-component[id^="GalleryThumbnails"]'
      )
      .forEach((component) => updateGalleryArrowState(component));
  }

  /*
   * The page-speed script can cause Shopify's slider component to miss its
   * own initialization. Handle gallery thumbnail arrows during capture so a
   * partially initialized native handler cannot also move the strip twice.
   */
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const arrowButton = event.target.closest(
        'media-gallery .slider-button--prev, ' +
          'media-gallery .slider-button--next'
      );

      if (
        !arrowButton ||
        !arrowButton
          .closest('slider-component')
          ?.id.startsWith('GalleryThumbnails')
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      moveGalleryThumbnailSlider(arrowButton);
    },
    true
  );

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const popupCloseButton = event.target.closest(
      '.product-popup-modal__toggle[id^="ModalClose-"]'
    );

    if (popupCloseButton) {
      event.preventDefault();
      closeProductVideoModal(popupCloseButton);
      return;
    }

    const popupOpener = event.target.closest(
      'modal-opener.product-popup-modal__opener'
    );

    if (popupOpener) {
      openProductVideoModal(popupOpener);
      return;
    }

    const videoButton = event.target.closest(
      '.video-label[data-videobtn]'
    );

    if (videoButton) {
      event.preventDefault();
      showVideo(videoButton);
      return;
    }

    const thumbnail = event.target.closest('button.thumbnail');

    if (!thumbnail) return;

    ensureGalleryMediaIsActive(thumbnail);

    /*
     * Selecting a thumbnail also selects its Color/Colour variant on mobile,
     * matching the behavior of the working backup.
     */
    if (!window.matchMedia('(max-width: 550px)').matches) return;

    const variantName = getThumbnailVariantName(thumbnail);
    if (!variantName) return;

    const section = getProductSection(thumbnail);
    const radio = findColorRadio(section, variantName);

    if (radio && !radio.disabled) {
      /*
       * Do not call radio.click(). This theme has a legacy inline onclick
       * which calls the missing openmblTab() function. Updating checked and
       * dispatching the standard events selects the variant without invoking
       * that broken legacy handler.
       */
      radio.checked = true;
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      console.warn(
        `No enabled Color/Colour option matches thumbnail "${variantName}".`
      );
    }
  });

  /*
   * Keep #defaultOption synchronized without binding directly to radio
   * elements that Shopify may later replace.
   */
  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const radio = event.target;

    if (radio.type !== 'radio' || !COLOR_OPTION_NAMES.has(radio.name)) {
      return;
    }

    const variantId = radio.dataset.variantid;
    if (!variantId) return;

    const section = getProductSection(radio);
    const defaultOption =
      section.querySelector('#defaultOption') ||
      document.querySelector('#defaultOption');

    if (defaultOption) {
      defaultOption.value = variantId;
      defaultOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /*
   * Handle both a normal page load and Shopify's theme-editor/AJAX section
   * loading. If this script is evaluated after DOMContentLoaded, initialize
   * immediately instead of waiting for an event that already happened.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeVideoControls();
        initializeGalleryArrowButtons();
      },
      { once: true }
    );
  } else {
    initializeVideoControls();
    initializeGalleryArrowButtons();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeVideoControls(event.target);
    initializeGalleryArrowButtons(event.target);
  });
})();
